import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import OfferCard from "@/components/OfferCard";
import { Search, Map, LayoutGrid, X } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, buildPageMeta } from "@/lib/seoIndex";
import { getLocalizedOfferTitle } from "@/lib/localizedOffer";

export default function OffersPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], cause_areas: [], countries: [] });
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid"); // grid | map

  const filters = useMemo(() => ({
    country: params.get("country") || "",
    city: params.get("city") || "",
    category: params.get("category") || "",
    cause_area: params.get("cause_area") || "",
    q: params.get("q") || "",
  }), [params]);

  const hasParams = Object.values(filters).some(Boolean);

  const offersMeta = buildPageMeta("offersList", { hasParams });
  useSEO({
    robots: decideRobots({ pageType: hasParams ? "search" : "offersList", hasParams }),
    title: offersMeta.title,
    description: offersMeta.description,
    // Canonical always points to the bare /offers — never to filter combinations.
    canonical: buildCanonical("/offers"),
  });

  const [cities, setCities] = useState([]);

  /* eslint-disable */
  useEffect(() => {
    api.get("/meta").then(r => setMeta(r.data)).catch(() => { /* swallow */ });
    api.get("/offers/cities").then(r => setCities(r.data)).catch(() => { /* swallow */ });
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = { limit: 1000 };
    Object.entries(filters).forEach(([k, v]) => { if (v) q[k] = v; });
    api.get("/offers", { params: q })
      .then(r => setOffers(r.data))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, [filters]);
  /* eslint-enable */

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const updateMany = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value); else next.delete(key);
    });
    setParams(next, { replace: true });
  };

  const clearAll = () => setParams({}, { replace: true });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // Localised region label for the heading ("in {city}, {country}", "in {country}", or "").
  const cityLabel = filters.city ? t(`cities.${filters.city}`, { defaultValue: filters.city }) : "";
  const countryLabel = filters.country ? t(`countries.${filters.country}`, { defaultValue: filters.country }) : "";
  const regionLabel = [cityLabel, countryLabel].filter(Boolean).join(", ");

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10" data-testid="offers-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="eyebrow">{t("offers.page_eyebrow")}</p>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight" data-testid="offers-page-title">
            {regionLabel
              ? t("offers.page_title_region", { region: regionLabel, defaultValue: "All offers in {{region}}" })
              : t("offers.page_title", { defaultValue: "All offers" })}
          </h1>
          <p className="mt-2 text-sm text-[#5F6B61]" data-testid="offers-count">
            {loading
              ? t("offers.loading")
              : regionLabel
              ? t("offers.count_region", { count: offers.length, region: regionLabel, defaultValue: "{{count}} offers in {{region}}" })
              : t("offers.count", { count: offers.length })}
          </p>
        </div>
        <div className="inline-flex rounded-full border border-[#DED7C7] bg-white p-1" data-testid="view-toggle">
          <button
            onClick={() => setView("grid")}
            className={`btn-pill px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 transition ${view==="grid" ? "bg-[#5B8C66] text-white" : "text-[#1F2A24]"}`}
            data-testid="view-grid-btn"
          >
            <LayoutGrid size={14} strokeWidth={2}/> {t("offers.view_grid")}
          </button>
          <button
            onClick={() => setView("map")}
            className={`btn-pill px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 transition ${view==="map" ? "bg-[#5B8C66] text-white" : "text-[#1F2A24]"}`}
            data-testid="view-map-btn"
          >
            <Map size={14} strokeWidth={2}/> {t("offers.view_map")}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-3">
          <div className="card-clay p-6 sticky top-24" data-testid="filters-panel">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold">{t("offers.filters_title")}</h2>
              {activeFilterCount > 0 && (
                <button onClick={clearAll} className="text-xs text-[#5B8C66] font-bold inline-flex items-center gap-1" data-testid="filters-clear-btn">
                  <X size={12}/> {t("offers.filters_clear")}
                </button>
              )}
            </div>

            <Field label={t("offers.filter_search")}>
              <div className="relative">
                <Search size={14} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F6B61]"/>
                <input
                  type="search"
                  value={filters.q}
                  onChange={(e) => update("q", e.target.value)}
                  placeholder={t("offers.filter_search_placeholder")}
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
                  data-testid="filter-search-input"
                />
              </div>
            </Field>

            <Field label={t("offers.filter_category")}>
              <select
                value={filters.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
                data-testid="filter-category-select"
              >
                <option value="">{t("offers.filter_all_categories")}</option>
                {meta.categories.map(c => <option key={c.value} value={c.value}>{t(`categories.${c.value}`, { defaultValue: c.label })}</option>)}
              </select>
            </Field>

            <Field label={t("offers.filter_cause")}>
              <select
                value={filters.cause_area}
                onChange={(e) => update("cause_area", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
                data-testid="filter-cause-select"
              >
                <option value="">{t("offers.filter_all_causes")}</option>
                {meta.cause_areas.map(c => <option key={c} value={c}>{t(`causes.${c}`, { defaultValue: c })}</option>)}
              </select>
            </Field>

            <Field label={t("offers.filter_country")}>
              <select
                value={filters.country}
                onChange={(e) => updateMany({ country: e.target.value, city: "" })}
                className="w-full px-3 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
                data-testid="filter-country-select"
              >
                <option value="">{t("offers.filter_all_countries")}</option>
                {meta.countries.map(c => <option key={c} value={c}>{t(`countries.${c}`, { defaultValue: c })}</option>)}
              </select>
            </Field>

            <Field label={t("offers.filter_city")}>
              <select
                value={filters.city}
                onChange={(e) => update("city", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
                data-testid="filter-city-select"
              >
                <option value="">{t("offers.filter_all_cities")}</option>
                {cities
                  .filter(c => !filters.country || c.country === filters.country)
                  .map(c => <option key={`${c.country}-${c.city}`} value={c.city}>{c.city} ({c.count})</option>)}
              </select>
            </Field>
          </div>
        </aside>

        {/* Results */}
        <main className="lg:col-span-9">
          {view === "grid" ? (
            loading ? (
              <SkeletonGrid/>
            ) : offers.length === 0 ? (
              <Empty t={t}/>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="offers-grid">
                {offers.map(o => <OfferCard key={o.id} offer={o}/>)}
              </div>
            )
          ) : (
            <MapView offers={offers} t={t} i18n={i18n}/>
          )}
        </main>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="text-xs font-bold uppercase tracking-wider text-[#3F6147] mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
      {Array.from({length: 6}).map((_, i) => (
        <div key={i} className="card-clay overflow-hidden">
          <div className="aspect-[4/3] bg-[#EFEAE0] animate-pulse"/>
          <div className="p-5 space-y-3">
            <div className="h-4 w-24 bg-[#EFEAE0] rounded animate-pulse"/>
            <div className="h-5 bg-[#EFEAE0] rounded animate-pulse"/>
            <div className="h-3 w-32 bg-[#EFEAE0] rounded animate-pulse"/>
          </div>
        </div>
      ))}
    </div>
  );
}

function Empty({ t }) {
  return (
    <div className="card-clay p-12 text-center" data-testid="offers-empty">
      <p className="text-3xl mb-2">🔎</p>
      <h3 className="text-xl font-bold">{t("offers.empty_title")}</h3>
      <p className="text-sm text-[#5F6B61] mt-2">{t("offers.empty_body")}</p>
    </div>
  );
}

function MapView({ offers, t, i18n }) {
  // Stylized "map" using a CSS background + positioned pins by lat/lng (very approximate normalization)
  const minLat = 35, maxLat = 71, minLng = -10, maxLng = 32;
  const norm = (lat, lng) => {
    const x = ((lng - minLng) / (maxLng - minLng)) * 100;
    const y = (1 - (lat - minLat) / (maxLat - minLat)) * 100;
    return { x: Math.max(2, Math.min(98, x)), y: Math.max(2, Math.min(98, y)) };
  };

  return (
    <div className="card-clay overflow-hidden" data-testid="map-view">
      <div className="relative eu-map-bg aspect-[5/4]">
        {/* country dots scaffold */}
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full opacity-20" preserveAspectRatio="none">
          <defs>
            <pattern id="dots" width="3" height="3" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.5" fill="#3F6147"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#dots)"/>
        </svg>

        {offers.filter(o => o.lat && o.lng).map((o) => {
          const { x, y } = norm(o.lat, o.lng);
          return (
            <a key={o.id} href={`/offer/${o.id}`} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{ left: `${x}%`, top: `${y}%` }} data-testid={`map-pin-${o.id}`}>
              <span className="relative flex">
                <span className="absolute inline-flex h-4 w-4 rounded-full bg-[#5B8C66] opacity-60 animate-ping"/>
                <span className="relative inline-flex w-3.5 h-3.5 rounded-full bg-[#5B8C66] border-2 border-white shadow"/>
              </span>
              <span className="hidden group-hover:block absolute left-4 top-0 z-10 bg-white border border-[#DED7C7] rounded-xl shadow-lg p-3 w-56">
                <p className="text-xs font-bold uppercase tracking-wider text-[#3F6147]">{t(`countries.${o.country}`, { defaultValue: o.country })}</p>
                <p className="text-sm font-semibold mt-1 line-clamp-2">{getLocalizedOfferTitle(o, i18n.language)}</p>
                <p className="text-xs text-[#5F6B61] mt-1">{o.organization}</p>
              </span>
            </a>
          );
        })}
      </div>
      <div className="p-5 border-t border-[#DED7C7] flex items-center justify-between text-sm">
        <span className="text-[#5F6B61]">{t("offers.map_count", { count: offers.length })}</span>
        <span className="inline-flex items-center gap-1.5 text-xs eyebrow">
          <span className="w-2 h-2 rounded-full bg-[#5B8C66]"/> {t("offers.map_legend")}
        </span>
      </div>
    </div>
  );
}
